import React, { useState } from 'react';
import './ReportsConfigPage.css';
import {
  Calendar, Download, FileArchive, Landmark, LucideIcon,
} from 'lucide-react';
import { useSiteContext } from '../../SiteContext';
import {
  downloadReportExcel, downloadReportExcelErrorMessage,
} from '../../api/reportsConfiguration';

/**
 * PRDA Monthly Reports (WCDP) — month/year picker plus two .zip downloads served by
 * GET admin/reports/run/{reportKey}/excel?month=&year=. Reuses the Reports
 * Configuration page's stylesheet (.rptcfg scope: page-head, panel, runrow,
 * run-period, whobtn) rather than adding a new one.
 */

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const MONTHS_SHORT = MONTHS.map((m) => m.slice(0, 3));

interface MonthlyReport {
  key: string;
  title: string;
  icon: LucideIcon;
  hint: (label: string) => string;
}

const REPORTS: MonthlyReport[] = [
  {
    key: 'WCDP_PRDA_MONTHLY',
    title: 'PRDA Monthly Filing',
    icon: FileArchive,
    hint: (label) => `Stored PRDA records for reporting month ${label}. Contains SSNs.`,
  },
  {
    key: 'WCDP_PW_MONTHLY_PROJECT_HOURS',
    title: 'Prevailing Wage Project Hours',
    icon: Landmark,
    hint: (label) =>
      `Pay periods with check dates in ${label}; all hours worked on prevailing-wage projects, one file set per project. Contains SSNs.`,
  },
];

/** Previous calendar month, as 1-based month + year. */
function defaultMonthYear(): { month: number; year: number } {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return { month: d.getMonth() + 1, year: d.getFullYear() };
}

const wrapRow: React.CSSProperties = { flexWrap: 'wrap' };

const PrdaMonthlyReportsPage: React.FC = () => {
  const { createPopup } = useSiteContext();
  const initial = defaultMonthYear();
  const [month, setMonth] = useState<number>(initial.month);
  const [year, setYear] = useState<number>(initial.year);
  const [running, setRunning] = useState<Record<string, boolean>>({});

  const currentYear = new Date().getFullYear();
  const years = [currentYear, currentYear - 1, currentYear - 2];
  const label = `${MONTHS_SHORT[month - 1]} ${year}`;
  const anyRunning = Object.values(running).some(Boolean);

  const doRun = async (reportKey: string) => {
    setRunning((prev) => ({ ...prev, [reportKey]: true }));
    try {
      await downloadReportExcel(reportKey, { month, year });
    } catch (e) {
      createPopup('Couldn\'t build the report', await downloadReportExcelErrorMessage(e));
    } finally {
      setRunning((prev) => ({ ...prev, [reportKey]: false }));
    }
  };

  return (
    <div className="rptcfg">
      <div className="page-head">
        <span className="ph-ic"><FileArchive size={23} /></span>
        <div>
          <h1>PRDA Monthly Reports</h1>
          <div className="ph-sub">
            Download the monthly PRDA filing and prevailing-wage project hours as .zip files.
            Nothing is emailed. Both files contain SSNs — store and share them accordingly.
          </div>
        </div>
      </div>

      <div className="panel">
        <div className="p-head">
          <Calendar size={16} className="p-ic" /><h3>Reporting month</h3>
        </div>
        <div className="p-body">
          <div className="runrow" style={wrapRow}>
            <select
              className="run-period"
              aria-label="Month"
              value={month}
              disabled={anyRunning}
              onChange={(e) => setMonth(Number(e.target.value))}
            >
              {MONTHS.map((m, i) => (
                <option key={m} value={i + 1}>{m}</option>
              ))}
            </select>
            <select
              className="run-period"
              aria-label="Year"
              value={year}
              disabled={anyRunning}
              onChange={(e) => setYear(Number(e.target.value))}
            >
              {years.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <span className="run-hint">Defaults to the previous calendar month.</span>
          </div>
        </div>
      </div>

      {REPORTS.map((r) => {
        const Icon = r.icon;
        const isRunning = !!running[r.key];
        return (
          <div className="panel" key={r.key}>
            <div className="p-head">
              <Icon size={16} className="p-ic" /><h3>{r.title}</h3>
            </div>
            <div className="p-body">
              <div className="runrow" style={wrapRow}>
                <span className="run-hint">{r.hint(label)}</span>
                <button
                  type="button"
                  className="whobtn"
                  disabled={isRunning}
                  onClick={() => doRun(r.key)}
                >
                  <Download size={15} />{isRunning ? 'Building…' : 'Download .zip'}
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PrdaMonthlyReportsPage;
